import React from 'react'
import { BsCreditCard } from "react-icons/bs";
import PixGray from "assets/images/icons/pix-gray.svg"
import { checkFlag, creditCardSensibleMask, formatCurrency, truncTwoDecimals } from "utils";
import { Pencil } from "lucide-react";
import { StatusType } from "hooks/usePayment";
import { CardLastDigits, CircleSkeleton, DebtInfo, EditButton, EditButtonSkeleton, PaymentCard, PaymentDescription, PaymentIconWrapper, PaymentInfo, PaymentStatusWrapper, RectangleSkeleton, StatusSkeleton, StyledBadge, UserIconWrapper } from "./styles";
import { EnumPaymentMethod } from "../FormPayment/types";

const paymentIconDict = {
  "credit": BsCreditCard,
  "debit": BsCreditCard,
  "pix": () => <img src={PixGray} height={24} />,
}

interface CardPaymentInfoProps {
  type?: "credit" | "debit" | "pix";
  lastDigits?: string;
  cardNumber?: string;
  isLoading?: boolean;
  value?: number;
  onEdit?: () => void;
  status?: StatusType;
  description: string;
  fee?: number;
  onClick?: () => void;
}

interface StatusBadgeProps {
  status: StatusType
  className?: string
}

const StatusBadge: React.FC<StatusBadgeProps> = ({ status, className }) => {
  const getStatusLabel = (status: StatusType): string => {
    switch (status) {
      case "started":
        return "Iniciado"
      case "success":
        return "Feito"
      case "error":
        return "Falha no pagamento"
      default:
        return "Aguardando"
    }
  }

  return (
    <StyledBadge $status={status} className={className}>
      {getStatusLabel(status)}
    </StyledBadge>
  )
}

const LabelComponent = ({ type, cardNumber }: Omit<CardPaymentInfoProps, "amount" | "description">) => {
  if (!type) return null;
  if (type === EnumPaymentMethod.PIX) return <span>Pix</span>;

  return <CardLastDigits><img src={checkFlag({ cardNumber: cardNumber })} style={{ width: "28px" }} /> {creditCardSensibleMask(cardNumber || "", "•")}</CardLastDigits>
}

const DescriptionComponent = ({ type, value = 0, fee = 0, description }: Omit<CardPaymentInfoProps, "amount"> & { fee?: number }) => {
  if (!type) return null

  if (type !== EnumPaymentMethod.CREDITO) return <PaymentDescription>{formatCurrency(truncTwoDecimals(value))} + {formatCurrency(truncTwoDecimals(fee))} = {description}</PaymentDescription>

  return <PaymentDescription>{formatCurrency(truncTwoDecimals(value))} + {formatCurrency(truncTwoDecimals(fee))} = {description}</PaymentDescription>
}

const CardPaymentInfo = ({ type, cardNumber, value = 0, onEdit, status, fee, onClick, description = "", isLoading = false }: CardPaymentInfoProps) => {
  const PaymentIcon = paymentIconDict[type as keyof typeof paymentIconDict]

  if (isLoading) {
    return <CardPaymentInfoLoading showEditButton={!!onEdit} showStatus={!!status} />
  }

  return (
    <PaymentCard status={status} onClick={() => onClick?.()}>
      <UserIconWrapper>
        <PaymentIcon height={24} color="#999" />
      </UserIconWrapper>
      <DebtInfo>
        <PaymentIconWrapper>
          <LabelComponent type={type} cardNumber={cardNumber} />
        </PaymentIconWrapper>
        <PaymentInfo>
          <DescriptionComponent type={type} value={value} fee={fee} description={description} />
        </PaymentInfo>
      </DebtInfo>
      {onEdit && <EditButton onClick={onEdit}>
        <Pencil size={16} color="#666" />
      </EditButton>}
      {status &&
        <PaymentStatusWrapper>
          <StatusBadge status={status} />
        </PaymentStatusWrapper>
      }
    </PaymentCard>
  )
}

interface CardPaymentInfoLoadingProps {
  showEditButton?: boolean
  showStatus?: boolean
}

const CardPaymentInfoLoading: React.FC<CardPaymentInfoLoadingProps> = ({
  showEditButton = false,
  showStatus = false,
}) => {
  return (
    <PaymentCard style={{ width: "400px" }}>
      <UserIconWrapper>
        <CircleSkeleton />
      </UserIconWrapper>

      <DebtInfo>
        <PaymentIconWrapper>
          <RectangleSkeleton width="24px" height="24px" />
          <RectangleSkeleton width="120px" height="14px" />
        </PaymentIconWrapper>

        <PaymentInfo>
          <RectangleSkeleton width="80%" height="14px" />
        </PaymentInfo>
      </DebtInfo>

      {showEditButton && <EditButtonSkeleton />}

      {showStatus && (
        <PaymentStatusWrapper>
          <StatusSkeleton />
        </PaymentStatusWrapper>
      )}
    </PaymentCard>
  )
}

export default CardPaymentInfo